import type { Plan } from '@platform/types';
import { Check, MessageSquare, Phone } from 'lucide-react';

import { formatMoney } from '@/lib/currency';

/**
 * Feature comparison under the plan cards. Deliberately honest: every plan
 * ships every feature on both channels — tiers differ only in included voice
 * minutes, chat messages, and the chat spend cap. The table makes that a
 * selling point instead of inventing artificial gating.
 */
const SHARED_FEATURES = [
  '24/7 AI voice agent (phone)',
  'Embeddable website chat widget',
  'One knowledge base across both channels (RAG)',
  'Local number (US, UK, AU, NZ)',
  'Transcripts & AI summaries',
  'Unlimited team members',
  'Analytics dashboard',
  'Webhook export',
];

export function PlanComparison({ plans }: { plans: Plan[] }): JSX.Element | null {
  const ordered = [...plans].sort((a, b) => a.sortOrder - b.sortOrder);
  if (ordered.length === 0) return null;

  return (
    <div className="mx-auto mt-16 max-w-4xl overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="mb-4 text-left text-lg font-semibold">
          Compare plans
        </caption>
        <thead>
          <tr className="border-b border-border text-left">
            <th scope="col" className="py-3 pr-4 font-medium text-muted-foreground">
              &nbsp;
            </th>
            {ordered.map((p) => (
              <th key={p.id} scope="col" className="px-4 py-3 font-semibold">
                {p.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Price / month
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                {p.isTrial ? 'Free' : formatMoney(p.priceCents, p.currency)}
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Channels
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                    <Phone className="h-3 w-3" />
                    Voice
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <MessageSquare className="h-3 w-3" />
                    Chat
                  </span>
                </div>
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Inbound voice minutes
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                {p.voiceMinutesIncluded}
                {p.isTrial && p.trialDays ? ` over ${p.trialDays} days` : ' / month'}
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Chat messages
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                {p.chatMessagesIncluded}
                {p.isTrial && p.trialDays ? ` over ${p.trialDays} days` : ' / month'}
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Always-on
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                24/7/365
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Concurrency
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                20 calls at once
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Transfer to a human
              <span className="block text-xs font-normal text-muted-foreground">
                Callers who ask for a person are forwarded to your verified number
              </span>
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                {p.callTransferEnabled ? (
                  <Check aria-label="Included" className="h-4 w-4 text-emerald-500" />
                ) : (
                  <span aria-label="Not included" className="text-muted-foreground">
                    —
                  </span>
                )}
              </td>
            ))}
          </tr>
          <tr className="border-b border-border">
            <th scope="row" className="py-3 pr-4 text-left font-medium">
              Chat AI-spend cap
            </th>
            {ordered.map((p) => (
              <td key={p.id} className="px-4 py-3">
                {p.chatDailySpendCapCents > 0
                  ? `$${(p.chatDailySpendCapCents / 100).toFixed(0)} / day`
                  : 'Unlimited'}
              </td>
            ))}
          </tr>
          {SHARED_FEATURES.map((feature) => (
            <tr key={feature} className="border-b border-border">
              <th scope="row" className="py-3 pr-4 text-left font-normal text-muted-foreground">
                {feature}
              </th>
              {ordered.map((p) => (
                <td key={p.id} className="px-4 py-3">
                  <Check aria-label="Included" className="h-4 w-4 text-emerald-500" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-muted-foreground">
        Every plan includes every feature on both voice and chat — tiers differ only in
        included inbound voice minutes, chat messages, and the chat AI-spend cap.
      </p>
    </div>
  );
}
